import cls from 'classnames';
import chunk from 'lodash/chunk';
import round from 'lodash/round';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useResizeObserver from 'use-resize-observer';

import {
  requestVideoPlayerCurrentTime,
  updateVideoPlayerSourceFromClip,
} from 'actions/videoPlayer';
import {
  CLIP_VIDEO_SOURCE_URL_UPDATED,
  TRIM_CHANGE_MADE,
} from 'constants/events';
// @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
import { PLAYER_TIME_PRECISION } from 'constants/video';
import { selectVideoId } from 'selectors/video';
import { selectPaused } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { toHslaString } from 'utilities/colors';

import useStateRef from 'hooks/useStateRef';
import useThemeColors from 'hooks/useThemeColors';

import ClipSection, { ClipSectionDef } from './clip-section';
import { useClipsState } from './clips/state';
import {
  WAVEFORM_FAILURE,
  WAVEFORM_IN_PROGRESS,
} from '../../../../../shared/constants/video';
import { PlayRange } from '../../../../../shared/types/playRanges';

import styles from './styles.module.less';

const MIN_TEXT_WIDTH = 120;
const TEXT_PADDING = 12;
const NO_AUDIO_TEXT = 'No audio';
const WAVEFORM_NOT_AVAILABLE_TEXT = 'Audio waveform not available';
const GENERATING_WAVEFORM_TEXT = 'Generating audio waveform...';

interface Props {
  clipId: string;
  peaks: number[];
  cursorPos: number;
  duration: number;
  playRanges: PlayRange[];
  isFocusedClip: boolean;
  updatePlayRange: (range: PlayRange) => void;
  viewboxHeight?: number;
  peakCount?: number;
  cursorWidth?: number;
  normalize?: boolean;
  onClick?: (
    event: React.MouseEvent,
    extra: { secs: number; clipIdx: number }
  ) => void;
  onDoubleClick?: (event: React.MouseEvent, extra: { secs: number }) => void;
  cursorVerticalMargin?: number;
  sectionOutlineWidth?: number;
  selectedPlayRange?: number | null;
  waveformStatus: string | null;
  hasAudio: boolean;
}

function Waveform({
  clipId,
  viewboxHeight = 80,
  peakCount = 200,
  cursorWidth = 2,
  peaks,
  cursorPos,
  duration,
  normalize = false,
  isFocusedClip = false,
  cursorVerticalMargin = 16,
  sectionOutlineWidth = 2,
  onClick = () => {
    return;
  },
  onDoubleClick = () => {
    return;
  },
  playRanges,
  selectedPlayRange,
  updatePlayRange,
  waveformStatus,
  hasAudio,
}: Props): JSX.Element {
  //
  // ─── SETUP AND INITIAL VARIABLES ────────────────────────────────────────────────
  //

  const waveformHeight = viewboxHeight - 2 * cursorVerticalMargin;
  const { ref: resizeRef, width: pixelWidth } =
    useResizeObserver<HTMLDivElement>();

  /**
   * When this component first loads, the pixelWidth will be undefined.
   * The following or statement prevents the SVG from throwing a lot of
   * errors based off that undefined width.
   */
  const viewboxWidth = pixelWidth || 1;
  const lineWidth = viewboxWidth / peakCount;

  const colors = useThemeColors();

  // This color is calculated using an RGB value b/c SVG layering makes it
  // not match with HLSA colors.
  const playedWaveformColor = '#A9A5F9';
  const playedBackgroundColor = toHslaString({ ...colors.primary, a: 0.1 });
  const trimmedWaveformColor = toHslaString({
    ...colors.disabledContent,
    a: 0.3,
  });
  const trimmedBackgroundColor = toHslaString(colors.backgroundSecondary);
  const toPlayWaveformColor = toHslaString(colors.disabledContent);
  const playheadColor = toHslaString(colors.primaryActive);
  const hoverPlayheadColor = toHslaString({
    ...colors.primaryActive,
    a: 0.5,
  });

  const svgXToSecs = x =>
    round(x * (duration / viewboxWidth), PLAYER_TIME_PRECISION);
  const secsToSvgX = useCallback(
    secs => {
      if (secs == null) {
        secs = 0;
      }

      if (duration === 0) {
        return 0;
      }

      return (secs / duration) * viewboxWidth;
    },
    [duration, viewboxWidth]
  );
  const dispatch = useDispatch();

  if (!playRanges || playRanges.length === 0) {
    playRanges = [{ id: 'default', from: 0, to: duration }];
  }

  const posInPlayRange = pos =>
    playRanges.find(({ from, to }) => pos >= from && pos <= to);

  const svgEl = useRef(null);
  const pt = useRef(null);
  const textRef = useRef(null);
  const [hoverPos, setHoverPos] = useState<number>();
  const [showText, setShowText] = useState<boolean>(true);
  const [textToShow, setTextToShow] = useState<string>(
    GENERATING_WAVEFORM_TEXT
  );
  const [textWidth, setTextWidth, textWidthRef] =
    useStateRef<number>(MIN_TEXT_WIDTH);

  const [isMovingHandle, setIsMovingHandle] = useState<boolean>(false);
  const [cursorPosPx, setCursorPosPx] = useState<number>(0);
  const [hideHoverPlayhead, setHideHoverPlayhead] = useState<boolean>(false);
  const [hovering, setHovering] = useState<boolean>(false);
  const videoId = useSelector(selectVideoId);
  const paused = useSelector(selectPaused);

  const { clips, focusedClipIndex, updateFocusedClipIndex } = useClipsState(
    state => ({
      clips: state.clips,
      focusedClipIndex: state.focusedClipIndex,
      updateFocusedClipIndex: state.updateFocusedClipIndex,
    })
  );

  // This string and all references to it below are because play ranges
  // can be empty in the case of a null duration. In that case, we enter
  // an infinite re-render as the empty array is compared to an empty
  // array over and over. Stringifying this value is not optimal, but
  // a way to solve this problem.
  // Alternatively, we could use this hook library:
  // https://github.com/kentcdodds/use-deep-compare-effect
  const playRangesJSONString = JSON.stringify(playRanges);

  useEffect(() => {
    const playRanges = JSON.parse(playRangesJSONString);

    if (!playRanges[0] || viewboxWidth === 1) {
      return;
    }

    const firstPlayRangeFromX = secsToSvgX(playRanges[0].from);
    const firstPlayRangeToX = secsToSvgX(playRanges[0].to);

    if (
      firstPlayRangeFromX >= TEXT_PADDING ||
      firstPlayRangeToX <= textWidthRef.current + TEXT_PADDING * 2
    ) {
      setShowText(false);
      setTextToShow('');

      return;
    }

    if (!hasAudio) {
      setShowText(true);
      setTextToShow(NO_AUDIO_TEXT);
    } else if (waveformStatus === WAVEFORM_FAILURE || waveformStatus === null) {
      setShowText(true);
      setTextToShow(WAVEFORM_NOT_AVAILABLE_TEXT);
    } else if (waveformStatus === WAVEFORM_IN_PROGRESS) {
      setShowText(true);
      setTextToShow(GENERATING_WAVEFORM_TEXT);
    }
  }, [
    playRangesJSONString,
    hasAudio,
    secsToSvgX,
    viewboxWidth,
    waveformStatus,
    textWidthRef,
  ]);

  useEffect(() => {
    if (isMovingHandle) {
      return;
    }

    const newX = secsToSvgX(cursorPos);

    setCursorPosPx(newX);
  }, [cursorPos, isMovingHandle, secsToSvgX]);

  useEffect(() => {
    // @ts-expect-error ignore due to enabling strict null checks
    if (textRef.current && textRef.current.getComputedTextLength() > 0) {
      // @ts-expect-error ignore due to enabling strict null checks
      setTextWidth(textRef.current.getComputedTextLength());
    }
  }, [setTextWidth, textToShow, viewboxWidth]);

  useEffect(() => {
    // @ts-expect-error ignore due to enabling strict null checks
    pt.current = svgEl.current?.createSVGPoint();
  }, [svgEl]);

  /*
  Both of these values are derived from props. We put them in state as an
  optimization -- we may have thousands of peaks, and we don't need to recalc
  these values unless the peaks change.
  */
  const [maxAmplitude, setMaxAmplitude] = useState(1);
  const [minMaxPeaks, setMinMaxPeaks] = useState([]);

  /*
  This piece of state is here so we can keep the masks -- which show the gray
  or white background of the clip sections -- in sync as the user is dragging
  the actual sections.
  */
  const [curClipSections, setCurClipSections] = useState<{
    [id: string]: { leftX: number; rightX: number };
  }>({});
  const updateCurClipSection = ({ id, leftX, rightX }) => {
    setIsMovingHandle(true);

    setCurClipSections({
      ...curClipSections,
      [id]: { leftX, rightX },
    });
  };

  useEffect(() => {
    const playRanges = JSON.parse(playRangesJSONString);

    if (playRanges.length > 0) {
      setCurClipSections(
        playRanges.reduce((sections, { id, from, to }) => {
          return {
            ...sections,
            [id]: { leftX: secsToSvgX(from), rightX: secsToSvgX(to) },
          };
        }, {})
      );
    }
  }, [playRangesJSONString, secsToSvgX]);

  useEffect(() => {
    /*
    If we have chosen to normalize the waveform, we want its
    maximum amplitude to be used to determine the height of
    the waveform instead of a scale of -1 to 1.
     */
    setMaxAmplitude(normalize ? Math.max(...peaks.map(Math.abs)) : 1);
  }, [peaks, normalize]);

  useEffect(() => {
    const chunkSize = Math.round(peaks.length / peakCount);
    const chunkedPeaks = chunk(peaks, chunkSize);

    setMinMaxPeaks(
      // @ts-expect-error ignore due to enabling strict null checks
      chunkedPeaks.map(peaks => {
        const minP = Math.min(0, ...peaks);
        const maxP = Math.max(0, ...peaks);

        return [minP, maxP];
      })
    );
  }, [peaks, viewboxWidth, peakCount]);

  //
  // ─── HELPER FUNCTIONS ───────────────────────────────────────────────────────────
  //

  /**
   * Click events in JS give us the location relative to the browser window,
   * but we need the location in the SVG's coordinate system.
   * {@link https://stackoverflow.com/questions/29261304/how-to-get-the-click-coordinates-relative-to-svg-element-holding-the-onclick-lis}
   */
  const findSvgCoords = useCallback((evt: React.MouseEvent) => {
    if (!svgEl.current) {
      return;
    }

    if (pt.current !== undefined) {
      // @ts-expect-error ignore due to enabling strict null checks
      pt.current.x = evt.clientX;
      // @ts-expect-error ignore due to enabling strict null checks
      pt.current.y = evt.clientY;

      // The cursor point, translated into svg coordinates
      // @ts-expect-error ignore due to enabling strict null checks
      const cursorpt = pt.current.matrixTransform(
        // @ts-expect-error ignore due to enabling strict null checks
        svgEl.current.getScreenCTM().inverse()
      );

      return cursorpt;
    }
  }, []);

  /**
   * Transforms peak from number [-1, 1] to a pixel y position.
   */
  const transformPeak = peak => {
    let normedPeak = peak / maxAmplitude;

    // padding added to give a little breathing room
    const usableHeight = Math.floor(waveformHeight * 0.95);
    const padding = (waveformHeight - usableHeight) / 2;

    // Add 1 so the peak is 0 to 2 instead of -1 to 1.
    normedPeak = normedPeak + 1;
    normedPeak = normedPeak * (usableHeight / 2);
    normedPeak = usableHeight - normedPeak + padding;

    return normedPeak;
  };

  /**
   * This function creates the content for the `d` attribute on an SVG `path`
   * element. To draw the waveform, we need to draw the top from left-to-right
   * and then the bottom from right-to-left. In order to prevent multiple loops,
   * we collect the commands for the top and bottom in one loop and then reverse
   * the commands for the bottom before concatenating them all together.
   */
  const createWaveformPath = peaks => {
    if (!peaks || !peaks.length || lineWidth <= 0 || isNaN(lineWidth)) {
      return '';
    }

    let svgPath: string[] = [];
    const pathBack: string[] = [];

    svgPath.push(`M 0, ${transformPeak(peaks[0][1])}`);

    for (let idx = 0; idx < peaks.length; idx++) {
      const [minP, maxP] = peaks[idx];

      if (idx > 0) {
        svgPath.push(`L ${idx * lineWidth} ${transformPeak(maxP)}`);
      }

      if (idx < peaks.length - 1) {
        pathBack.push(`L ${idx * lineWidth} ${transformPeak(minP)}`);
      }
    }

    svgPath.push(
      `L ${(peaks.length - 1) * lineWidth}, ${transformPeak(
        peaks[peaks.length - 1][0]
      )}`
    );
    pathBack.reverse();
    svgPath = svgPath.concat(pathBack);
    svgPath.push('Z');

    return svgPath.join('\n');
  };

  //
  // ─── RENDERING CODE ─────────────────────────────────────────────────────────────
  //

  const handleSvgClick = e => {
    if (onClick) {
      const { x, y } = findSvgCoords(e);
      const secs = svgXToSecs(x);

      e.svgX = x;
      e.svgY = y;
      e.pos = secs;

      const clipIdx = clips.findIndex(clip => clip.id === clipId);
      const currentClip = clips[clipIdx];
      const previousClip = clips[focusedClipIndex];

      if (clipIdx !== focusedClipIndex) {
        updateFocusedClipIndex(clipIdx);

        dispatch(
          updateVideoPlayerSourceFromClip({
            sourceUrl: currentClip?.source_url,
            sourceMimeType: currentClip?.source_mime_type,
            partCredentials: currentClip?.source_part_credentials,
            sourceUrlIsTranscoded: currentClip?.source_url_is_transcoded,
            autoplay: !paused,
            requestPlay:
              !paused && previousClip?.source_url === currentClip?.source_url,
            seekTime: secs,
          })
        );

        analytics.track(CLIP_VIDEO_SOURCE_URL_UPDATED, {
          id: videoId,
          clipId: currentClip?.id,
          event_type: 'click',
        });
      }

      onClick(e, { secs: e.pos, clipIdx });
    }
  };

  const handleSvgDblClick = e => {
    if (onDoubleClick) {
      const { x, y } = findSvgCoords(e);

      e.svgX = x;
      e.svgY = y;
      e.pos = svgXToSecs(x);
      onDoubleClick(e, { secs: e.pos });
    }
  };

  const handleHandleGrab = () => {
    setHideHoverPlayhead(true);
  };

  const handleHandleRelease = ({ id, leftX, rightX, activeHandle }) => {
    const from = svgXToSecs(leftX);
    const to = svgXToSecs(rightX);

    setIsMovingHandle(false);

    const newX = activeHandle === 'left' ? leftX : rightX;
    const newSecs = svgXToSecs(newX);

    dispatch(requestVideoPlayerCurrentTime(newSecs));
    setCursorPosPx(newX);
    updatePlayRange({ id, from, to });
    setHideHoverPlayhead(false);

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      handleSide: activeHandle === 'left' ? 'start' : 'end',
      type: 'handle',
      hotkey: false,
    });
  };

  const handleMouseOver = () => {
    setHovering(true);
  };

  const handleMouseOut = () => {
    setHovering(false);
  };

  const shouldShowHoverPlayhead = hovering && !hideHoverPlayhead;

  // These two functions use JavaScript's odd handling of out-of-bounds array
  // indexes to set minimum and maximum bounds for clip sections. If you use
  // an out-of-bounds index -- even a negative number -- you get undefined.
  const calculateMinX = (playRanges: PlayRange[], idx: number) =>
    secsToSvgX(playRanges[idx - 1]?.to ?? 0);

  const calculateMaxX = (playRanges: PlayRange[], idx: number) =>
    secsToSvgX(playRanges[idx + 1]?.from ?? duration);

  interface WaveformSvgAttrs {
    onClick?: React.MouseEventHandler;
    onDoubleClick?: React.MouseEventHandler;
  }

  const svgAttrs: WaveformSvgAttrs = {};

  // @ts-expect-error ignore due to enabling strict null checks
  if (onClick) {
    svgAttrs.onClick = handleSvgClick;
  }

  // @ts-expect-error ignore due to enabling strict null checks
  if (onDoubleClick) {
    svgAttrs.onDoubleClick = handleSvgDblClick;
  }

  /*
  Our SVG below uses masking to show the level of progress through the
  waveform. A mask of the waveform is created, then the SVG is drawn with two
  rectangles, the first from the beginning to the cursor and the second from
  the cursor to the end. These rectangles use our mask, so only the part of the
  rectangle that the mask covers will be shown.
  */
  return (
    <div ref={resizeRef} className={styles.waveformContainer}>
      <svg
        ref={svgEl}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${viewboxWidth} ${viewboxHeight}`}
        className={cls(styles.overflowVisible, styles.waveform)}
        preserveAspectRatio="none"
        onMouseMove={evt => {
          const { x } = findSvgCoords(evt);

          setHoverPos(svgXToSecs(x));
        }}
        {...svgAttrs}
      >
        {/* Hover playhead */}
        {posInPlayRange(hoverPos) && shouldShowHoverPlayhead && (
          <line
            x1={secsToSvgX(hoverPos)}
            x2={secsToSvgX(hoverPos)}
            y1="0"
            y2={viewboxHeight}
            stroke={hoverPlayheadColor}
            strokeWidth={1}
          />
        )}

        <svg
          x={0}
          width={viewboxWidth}
          y={cursorVerticalMargin}
          height={waveformHeight}
          className={styles.overflowVisible}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          <defs>
            {Object.entries(curClipSections).map(([id, { leftX, rightX }]) => (
              <ClipSectionDef
                clipId={`${clipId}-${id}`}
                startX={leftX}
                endX={rightX}
                key={`def-${id}`}
                height={waveformHeight}
              />
            ))}
            <mask id={`waveform-mask-${clipId}`}>
              <rect
                x={0}
                width={Math.max(0, viewboxWidth)}
                y="0"
                height={waveformHeight}
                fill="black"
              />
              {peaks.length > 0 ? (
                <>
                  <path fill="white" d={createWaveformPath(minMaxPeaks)} />
                  <line
                    className="center-line"
                    x1={0}
                    x2={viewboxWidth}
                    y1={waveformHeight / 2}
                    y2={waveformHeight / 2}
                    stroke="white"
                    strokeWidth="1"
                  />
                </>
              ) : (
                <>
                  {showText && (
                    <text
                      x={TEXT_PADDING}
                      y={waveformHeight / 2 + 1}
                      className={styles.noAudio}
                      fill="white"
                      dominantBaseline="middle"
                      ref={textRef}
                    >
                      {textToShow}
                    </text>
                  )}
                  <line
                    className="center-line"
                    x1={showText ? textWidth + TEXT_PADDING * 2 : 0}
                    x2={viewboxWidth}
                    y1={waveformHeight / 2 + 1}
                    y2={waveformHeight / 2 + 1}
                    stroke="white"
                    strokeDasharray="3"
                    strokeWidth="1"
                  />
                </>
              )}
            </mask>
            <mask id={`play-ranges-mask-${clipId}`}>
              <rect
                x={0}
                y={0}
                rx="6"
                width={Math.max(0, viewboxWidth)}
                height={waveformHeight + sectionOutlineWidth}
                fill="white"
              />
              {playRanges.map(({ id }) => (
                <use
                  key={`use-${clipId}-${id}`}
                  href={`#${clipId}-${id}`}
                  fill="black"
                />
              ))}
            </mask>
            <mask id={`played-area-mask-${clipId}`}>
              <use href="#whole-waveform" fill="black" />
              {playRanges.map(({ id }) => (
                <use
                  key={`use-${clipId}-${id}`}
                  href={`#${clipId}-${id}`}
                  fill="white"
                />
              ))}
              <rect
                x={cursorPosPx}
                width={Math.max(0, viewboxWidth - cursorPosPx)}
                y="0"
                height={waveformHeight}
                fill="black"
              />
            </mask>
          </defs>

          {/* Overall border */}
          <rect
            x={0}
            y={0}
            rx="6"
            width={Math.max(0, viewboxWidth)}
            height={waveformHeight + sectionOutlineWidth}
            fill="none"
            strokeWidth="1"
            stroke={toHslaString(colors.border)}
          />

          {/* Background color of trimmed areas */}
          <rect
            x={0}
            y={0}
            rx="6"
            width={Math.max(0, viewboxWidth)}
            height={waveformHeight + sectionOutlineWidth}
            fill={trimmedBackgroundColor}
            mask={`url(#play-ranges-mask-${clipId})`}
          />

          {/* Background color of untrimmed areas left of the playhead */}
          {isFocusedClip && (
            <rect
              x={0}
              y={0}
              rx="6"
              width={Math.max(0, viewboxWidth)}
              height={waveformHeight + sectionOutlineWidth}
              fill={playedBackgroundColor}
              mask={`url(#played-area-mask-${clipId})`}
            />
          )}
          {/* waveform */}
          <g mask={`url(#waveform-mask-${clipId})`}>
            <rect
              className="trimmed-waveform"
              x="0"
              width={Math.max(0, viewboxWidth)}
              y="0"
              height={waveformHeight}
              fill={trimmedWaveformColor}
            />
            {Object.entries(curClipSections).map(([id, { leftX, rightX }]) => (
              <rect
                className="to-play-waveform"
                x={leftX}
                width={Math.max(0, rightX - leftX)}
                y="0"
                height={waveformHeight}
                key={`wf-${id}`}
                fill={toPlayWaveformColor}
              />
            ))}
            {Object.entries(curClipSections)
              .filter(([, { leftX }]) => leftX < cursorPosPx)
              .map(([id, { leftX, rightX }]) => (
                <rect
                  className="played-waveform"
                  x={leftX}
                  width={Math.max(0, Math.min(cursorPosPx, rightX) - leftX)}
                  y="0"
                  height={waveformHeight}
                  key={`wf-${id}`}
                  fill={playedWaveformColor}
                />
              ))}
          </g>
          {/* Actual playhead */}
          {isFocusedClip && (
            <line
              x1={cursorPosPx}
              x2={cursorPosPx}
              y1={-cursorVerticalMargin}
              y2={viewboxHeight - cursorVerticalMargin}
              stroke={playheadColor}
              strokeWidth={cursorWidth}
            />
          )}
          {playRanges.map(
            ({ from, to, id }, idx) =>
              (!isFocusedClip || idx !== selectedPlayRange) && (
                <ClipSection
                  clipId={id}
                  startX={secsToSvgX(from)}
                  endX={secsToSvgX(to)}
                  minX={calculateMinX(playRanges, idx)}
                  maxX={calculateMaxX(playRanges, idx)}
                  key={`${from}-${to}`}
                  active={false}
                  height={waveformHeight + 1}
                  findSvgCoords={findSvgCoords}
                  svgXToSecs={svgXToSecs}
                  secsToSvgX={secsToSvgX}
                  onHandleRelease={handleHandleRelease}
                  onHandleMove={updateCurClipSection}
                  viewboxWidth={viewboxWidth}
                  outlineWidth={sectionOutlineWidth - 1}
                />
              )
          )}
          {/* .map is used here for ease of destructuring */}
          {isFocusedClip &&
            playRanges
              // @ts-expect-error ignore due to enabling strict null checks
              .slice(selectedPlayRange, selectedPlayRange + 1)
              .map(({ from, to, id }) => (
                <ClipSection
                  clipId={id}
                  startX={secsToSvgX(from)}
                  endX={secsToSvgX(to)}
                  // @ts-expect-error ignore due to enabling strict null checks
                  minX={calculateMinX(playRanges, selectedPlayRange)}
                  // @ts-expect-error ignore due to enabling strict null checks
                  maxX={calculateMaxX(playRanges, selectedPlayRange)}
                  key={`${from}-${to}`}
                  active={true}
                  height={waveformHeight}
                  findSvgCoords={findSvgCoords}
                  svgXToSecs={svgXToSecs}
                  secsToSvgX={secsToSvgX}
                  onHandleGrab={handleHandleGrab}
                  onHandleRelease={handleHandleRelease}
                  onHandleMove={updateCurClipSection}
                  viewboxWidth={viewboxWidth}
                  outlineWidth={sectionOutlineWidth}
                />
              ))}
        </svg>
      </svg>
    </div>
  );
}

export default Waveform;
